/**
 * SearchItemCardView — пользовательская карточка товара из базы истины
 * (M2-A, 2026-09-14).
 *
 * Рендерит DTO GET /api/v1/search/row/{row_id}/result:
 *   - шапка: название, sku, краткое описание;
 *   - секции (main / tech / packaging / usage / installation /
 *     storage_safety / extra) с человекочитаемыми подписями фактов
 *     (i18n: label_key -> search.fact.*; fallback — prettify ключа);
 *   - «Уверенность» (процент, сырой conf НЕ показывается);
 *   - «Что не подтверждено» (state=unconfirmed);
 *   - «Конфликты» (значения + правило разрешения, без молчаливого выбора).
 *
 * Никаких debug / search_log / LLM-промптов / сырых ключей: они в DTO
 * карточки отсутствуют по контракту бэкенда.
 */
import { useTranslation } from 'react-i18next';

import { statusLabel } from '@/components/search/reasonLabel';

import { cn } from '@/lib/utils';

// === DTO (зеркало backend src/services/search/v2/card.py) ===

export interface CardFact {
  key: string;
  label_key: string;
  value: string;
  unit: string | null;
  state: 'confirmed' | 'unconfirmed' | 'resolved';
}

export interface CardSection {
  key: string;
  facts: CardFact[];
}

export interface CardConflict {
  key: string;
  label_key: string;
  values: string[];
  status: 'unresolved' | 'resolved';
  resolved_value: string | null;
  resolved_rule_key: string | null;
}

export interface SearchItemCard {
  item_id: string;
  title: string | null;
  sku: string | null;
  short_description: string | null;
  sections: CardSection[];
  status: string;
  confidence: number | null;
  unconfirmed: CardFact[];
  conflicts: CardConflict[];
}

const SECTION_LABEL_KEYS: Record<string, string> = {
  main: 'search.card.section.main',
  tech: 'search.card.section.tech',
  packaging: 'search.card.section.packaging',
  usage: 'search.card.section.usage',
  installation: 'search.card.section.installation',
  storage_safety: 'search.card.section.storage_safety',
  extra: 'search.card.section.extra',
};

/** prettify-фолбэк: "fire_reaction" -> "Fire reaction". */
function prettifyKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function FactRow({ fact }: { fact: CardFact }) {
  const { t } = useTranslation();
  const label = t(fact.label_key, { defaultValue: prettifyKey(fact.key) });
  return (
    <div className="flex items-baseline gap-2 py-1 border-b border-surface-800/60 last:border-0">
      <span className="text-xs text-surface-400 min-w-[9rem] max-w-[14rem]">
        {label}
      </span>
      <span className="text-sm text-surface-100 flex-1">
        {fact.value}
        {fact.unit && <span className="text-surface-400"> {fact.unit}</span>}
      </span>
      {fact.state === 'unconfirmed' && (
        <span
          className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider
                     bg-warning-tint/15 text-warning"
        >
          {t('search.card.state.unconfirmed', { defaultValue: 'not confirmed' })}
        </span>
      )}
      {fact.state === 'resolved' && (
        <span
          className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider
                     bg-info-tint/15 text-info"
        >
          {t('search.card.state.resolved', { defaultValue: 'resolved' })}
        </span>
      )}
    </div>
  );
}

function ConflictRow({ conflict }: { conflict: CardConflict }) {
  const { t } = useTranslation();
  const label = t(conflict.label_key, { defaultValue: prettifyKey(conflict.key) });
  return (
    <div className="rounded-lg bg-surface-800/40 p-3 mb-2">
      <div className="text-xs text-surface-400 mb-1">{label}</div>
      <div className="text-sm text-surface-100">
        {conflict.values.join(' / ')}
      </div>
      {conflict.status === 'resolved' && conflict.resolved_value && (
        <div className="text-xs mt-1 text-info">
          {t('search.card.conflictResolved', {
            defaultValue: 'Selected value: {{value}}',
            value: conflict.resolved_value,
          })}
          {conflict.resolved_rule_key &&
            ' — ' +
              t(conflict.resolved_rule_key, {
                defaultValue: t('search.card.conflictRuleDefault'),
              })}
        </div>
      )}
      {conflict.status !== 'resolved' && (
        <div className="text-xs mt-1 text-warning">
          {t('search.card.conflictUnresolved', {
            defaultValue: 'Value could not be confirmed — sources disagree',
          })}
        </div>
      )}
    </div>
  );
}

export function SearchItemCardView({ card }: { card: SearchItemCard }) {
  const { t } = useTranslation();

  const statusText = statusLabel(t, card.status);

  return (
    <div>
      {/* Шапка карточки */}
      <div className="mb-4">
        {card.title && (
          <h3 className="text-base font-semibold text-surface-50">{card.title}</h3>
        )}
        <div className="flex items-center gap-3 mt-1">
          {card.sku && (
            <span className="text-xs text-surface-400 font-mono">
              {t('search.card.sku', { defaultValue: 'SKU' })}: {card.sku}
            </span>
          )}
          <span
            className={cn(
              'text-xs px-2 py-0.5 rounded uppercase tracking-wider',
              card.status === 'enriched'
                ? 'bg-success-tint/15 text-success'
                : card.status === 'partial'
                  ? 'bg-warning-tint/15 text-warning'
                  : 'bg-danger-tint/15 text-danger',
            )}
          >
            {statusText}
          </span>
          {typeof card.confidence === 'number' && (
            <span className="text-xs text-surface-400">
              {t('search.card.confidence', { defaultValue: 'Confidence' })}:{' '}
              {Math.round(card.confidence * 100)}%
            </span>
          )}
        </div>
        {card.short_description && (
          <p className="text-sm text-surface-300 mt-2">{card.short_description}</p>
        )}
      </div>

      {/* Секции */}
      {card.sections.map((section) => (
        <div key={section.key} className="mb-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-surface-400 mb-1">
            {t(SECTION_LABEL_KEYS[section.key] ?? 'search.card.section.extra')}
          </h4>
          <div>
            {section.facts.map((f) => (
              <FactRow key={`${f.key}-${f.value}`} fact={f} />
            ))}
          </div>
        </div>
      ))}

      {/* Что не подтверждено */}
      {card.unconfirmed.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-warning mb-1">
            {t('search.card.unconfirmedTitle', {
              defaultValue: 'What is not confirmed',
            })}
          </h4>
          <div className="text-sm text-warning/90">
            {card.unconfirmed
              .map((f) =>
                t(f.label_key, { defaultValue: prettifyKey(f.key) }),
              )
              .join(', ')}
          </div>
        </div>
      )}

      {/* Конфликты */}
      {card.conflicts.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-surface-400 mb-2">
            {t('search.card.conflictsTitle', { defaultValue: 'Conflicts' })}
          </h4>
          {card.conflicts.map((c) => (
            <ConflictRow key={c.key} conflict={c} />
          ))}
        </div>
      )}
    </div>
  );
}
